import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  CircularProgress,
  Alert,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent
} from '@mui/material';
import { SwapVert, DirectionsBus, Navigation as NavigationIcon, AccessTime, WbSunny, WbTwilight, NightsStay, Brightness7 } from '@mui/icons-material';
import { getBusStops, searchBusRoutes, getBusLiveList } from '../../api/bus';
import InfoModal from '../InfoModel';
import { pushGA4CustomEvent } from '../../Analytics';

const BusRouteSearch = ({ 
  updateBottomNavigationTab, 
  displayFullScreen,
  updateBusStatusPage,
  updateBusForumPage,
  setForum,
  savedSearchParams = { startStop: '', endStop: '', hasSearched: false },
  saveSearchParams
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [stops, setStops] = useState([]);
  const [startStop, setStartStop] = useState(savedSearchParams.startStop || '');
  const [endStop, setEndStop] = useState(savedSearchParams.endStop || '');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [busResults, setBusResults] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [searched, setSearched] = useState(savedSearchParams.hasSearched || false);
  const [busLiveList, setBusLiveList] = useState([]);
  const [infoModalProps, setInfoModalProps] = useState({open: false});
  const [timePeriodFilter, setTimePeriodFilter] = useState('all');
  const [currentTime, setCurrentTime] = useState(0);
  
  // Get the current time in seconds from midnight
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const secondsFromMidnight = Math.floor((now.getTime() - midnight.getTime()) / 1000);
    setCurrentTime(secondsFromMidnight);
    
    // Default to 'all' for simplicity
    setTimePeriodFilter('all');
  }, []);
  
  // Fetch all bus stops and bus live list on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stopsData, liveListData] = await Promise.all([
          getBusStops(),
          getBusLiveList()
        ]);
        
        setStops(stopsData);
        setBusLiveList(liveListData || []);
        setInitialLoading(false);
        
        // If we have saved search parameters with hasSearched=true, perform the search
        if (savedSearchParams.hasSearched && savedSearchParams.startStop && savedSearchParams.endStop) {
          performSearch(savedSearchParams.startStop, savedSearchParams.endStop);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setErrorMessage('Unable to load data. Please try again later.');
        setInitialLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleStartStopChange = (event) => {
    setStartStop(event.target.value);
    if (saveSearchParams) {
      saveSearchParams({
        ...savedSearchParams,
        startStop: event.target.value,
        endStop: endStop
      });
    }
  };

  const handleEndStopChange = (event) => {
    setEndStop(event.target.value);
    if (saveSearchParams) {
      saveSearchParams({
        ...savedSearchParams,
        startStop: startStop,
        endStop: event.target.value
      });
    }
  };

  const handleSwapStops = () => {
    const temp = startStop;
    setStartStop(endStop);
    setEndStop(temp);
    
    // Track swap stops event
    if (startStop && endStop) {
      const startStopName = stops.find(stop => stop.id === startStop)?.name || startStop;
      const endStopName = stops.find(stop => stop.id === endStop)?.name || endStop;
      
      pushGA4CustomEvent(
        "Swap Bus Stops", 
        "swapBusStops", 
        `${startStopName} to ${endStopName} swapped to ${endStopName} to ${startStopName}`
      );
    }
    
    if (saveSearchParams) {
      saveSearchParams({
        ...savedSearchParams,
        startStop: endStop,
        endStop: startStop
      });
    }
  };

  // Helper function to perform the search
  const performSearch = async (start, end) => {
    setLoading(true);
    setErrorMessage('');
    setBusResults([]);
    
    try {
      const results = await searchBusRoutes(start, end);
      setBusResults(results);
      setSearched(true);
      
      if (results.length === 0) {
        setErrorMessage('No direct buses found between these stops');
      }
      
      // Save the search parameters
      if (saveSearchParams) {
        saveSearchParams({
          startStop: start,
          endStop: end,
          hasSearched: true
        });
      }
    } catch (error) {
      console.error('Error searching bus routes:', error);
      setErrorMessage('Unable to search for bus routes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!startStop || !endStop) {
      setErrorMessage('Please select both start and destination bus stops');
      return;
    }

    if (startStop === endStop) {
      setErrorMessage('Start and destination stops cannot be the same');
      return;
    }

    // Track search event
    const startStopName = stops.find(stop => stop.id === startStop)?.name || startStop;
    const endStopName = stops.find(stop => stop.id === endStop)?.name || endStop;
    
    pushGA4CustomEvent(
      "Bus Route Search", 
      "busRouteSearch", 
      `${startStopName} to ${endStopName}, ${new Date().toLocaleTimeString()}`
    );

    performSearch(startStop, endStop);
  };

  // Format time (seconds from midnight) to AM/PM format
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  // Calculate the journey duration in minutes
  const calculateDuration = (startTime, endTime) => {
    const durationInSeconds = endTime - startTime;
    return Math.floor(durationInSeconds / 60);
  };

  // For the live button and info modal functionality
  const showModal = (bus) => {
    const busTime = formatTime(bus.departureTime);
    const busName = bus.busName;
    const routeName = bus.routeName;
    
    // Create the complete busId using routeId_departureTime format
    // Make sure departureTime is a number, not a string
    const departureTime = typeof bus.departureTime === 'string' 
      ? parseInt(bus.departureTime, 10) 
      : bus.departureTime;
      
    // Create the composite ID with underscore separator
    const busId = `${bus.routeId}_${departureTime}`;
    
    // Track bus selection event
    pushGA4CustomEvent(
      "Bus Selected From Search", 
      "busSelectedFromSearch", 
      `${busName}, ${busTime}, ${busId}, ${routeName}, ${stops.find(stop => stop.id === startStop)?.name || startStop} to ${stops.find(stop => stop.id === endStop)?.name || endStop}`
    );

    setInfoModalProps({
      title: `${busTime}  |  ${busName}`,
      message: `${routeName}`,
      open: true,
      closeInfoModal,
      updateBusStatusPage,
      updateBusForumPage,
      setForum,
      busId: busId,
      busInfo: `${busName} (${routeName})`
    });
  };

  const closeInfoModal = () => {
    setInfoModalProps({
      open: false
    });
  };

  const openLoginAlertBox = () => {
    // This function would be used for login alerts
    // Not implementing this since we're assuming the user is logged in
  };

  // Filter buses based on time period
  const getFilteredBusResults = () => {
    if (timePeriodFilter === 'all') {
      return busResults;
    }
    
    return busResults.filter(bus => {
      const hour = Math.floor(bus.departureTime / 3600);
      
      switch(timePeriodFilter) {
        case 'morning':
          return hour >= 4 && hour < 12;
        case 'afternoon':
          return hour >= 12 && hour < 16;
        case 'evening':
          return hour >= 16 && hour < 19;
        case 'night':
          return hour >= 19 || hour < 4;
        default:
          return true;
      }
    });
  };
  
  const handleTimePeriodChange = (period) => {
    setTimePeriodFilter(period);
    
    // Track time period filter change
    if (searched && busResults.length > 0) {
      pushGA4CustomEvent(
        "Bus Time Filter Change", 
        "busTimeFilterChange", 
        `${period} filter, ${stops.find(stop => stop.id === startStop)?.name || startStop} to ${stops.find(stop => stop.id === endStop)?.name || endStop}, ${busResults.length} results`
      );
    }
  };

  // Helper to determine which time period an hour belongs to
  const getTimePeriodForHour = (hour) => {
    // Morning: 4am to 11:59am
    if (hour >= 4 && hour < 12) return 'morning';
    
    // Afternoon: 12pm to 3:59pm
    if (hour >= 12 && hour < 16) return 'afternoon';
    
    // Evening: 4pm to 6:59pm
    if (hour >= 16 && hour < 19) return 'evening';
    
    // Night: 7pm to 3:59am
    return 'night';
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Paper elevation={1} sx={{
        p: isMobile ? 2 : 3,
        borderRadius: 2,
        mx: 'auto',
        maxWidth: '100%',
        background: '#fcfcfc'
      }}>
        <Box sx={{
          mt: 1,
          mb: 4,
          backgroundColor: '#fff',
          borderRadius: 2,
          p: 2,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
        }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.5
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="start-stop-label">From</InputLabel>
              <Select
                labelId="start-stop-label"
                id="start-stop"
                value={startStop}
                label="From"
                onChange={handleStartStopChange}
                sx={{
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.15)'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 300 }
                  }
                }}
              >
                {stops.map((stop) => (
                  <MenuItem key={stop.value} value={stop.value}>
                    {stop.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
              <Button
                onClick={handleSwapStops}
                sx={{
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(93, 74, 125, 0.08)',
                  color: '#5d4a7d',
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(93, 74, 125, 0.16)',
                  }
                }}
              >
                <SwapVert fontSize="small" />
              </Button>
            </Box>

            <FormControl fullWidth>
              <InputLabel id="end-stop-label">To</InputLabel>
              <Select
                labelId="end-stop-label"
                id="end-stop"
                value={endStop}
                label="To"
                onChange={handleEndStopChange}
                sx={{
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.15)'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 300 }
                  }
                }}
              >
                {stops.map((stop) => (
                  <MenuItem key={stop.value} value={stop.value}>
                    {stop.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading || !startStop || !endStop || startStop === endStop}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '8px',
                backgroundColor: '#5d4a7d',
                boxShadow: '0 1px 3px rgba(93, 74, 125, 0.3)',
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                letterSpacing: '0.3px',
                '&:hover': {
                  backgroundColor: '#4d3e69'
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(93, 74, 125, 0.3)',
                  color: 'white'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                'Search Buses'
              )}
            </Button>
          </Box>
        </Box>

        {errorMessage && (
          <Box mt={2} mb={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Alert 
              severity="warning"
              sx={{
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: '#f57c00'
                }
              }}
            >
              {errorMessage}
            </Alert>
          </Box>
        )}

        {searched && busResults.length > 0 && (
          <>
            <Divider sx={{ my: 3 }}>
              <Chip 
                label={`${busResults.length} Bus${busResults.length > 1 ? 'es' : ''} Found`}
                color="primary"
                sx={{ 
                  fontWeight: 500,
                  px: 2,
                  py: 0.5,
                  borderRadius: '16px',
                  backgroundColor: 'rgba(93, 74, 125, 0.08)',
                  color: '#5d4a7d',
                  border: '1px solid rgba(93, 74, 125, 0.3)',
                  '& .MuiChip-label': {
                    px: 1
                  }
                }}
              />
            </Divider>
            
            {/* Time period filter */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: 2,
              mt: 1
            }}>
              <Chip
                icon={<WbSunny sx={{ fontSize: '1rem' }} />}
                label="Morning"
                clickable
                onClick={() => handleTimePeriodChange('morning')}
                sx={{ 
                  backgroundColor: timePeriodFilter === 'morning' ? '#5d4a7d' : 'rgba(93, 74, 125, 0.08)',
                  color: timePeriodFilter === 'morning' ? 'white' : '#5d4a7d',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: timePeriodFilter === 'morning' ? '#4d3e69' : 'rgba(93, 74, 125, 0.15)'
                  }
                }}
              />
              <Chip
                icon={<Brightness7 sx={{ fontSize: '1rem' }} />}
                label="Afternoon"
                clickable
                onClick={() => handleTimePeriodChange('afternoon')}
                sx={{ 
                  backgroundColor: timePeriodFilter === 'afternoon' ? '#5d4a7d' : 'rgba(93, 74, 125, 0.08)',
                  color: timePeriodFilter === 'afternoon' ? 'white' : '#5d4a7d',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: timePeriodFilter === 'afternoon' ? '#4d3e69' : 'rgba(93, 74, 125, 0.15)'
                  }
                }}
              />
              <Chip
                icon={<WbTwilight sx={{ fontSize: '1rem' }} />}
                label="Evening"
                clickable
                onClick={() => handleTimePeriodChange('evening')}
                sx={{ 
                  backgroundColor: timePeriodFilter === 'evening' ? '#5d4a7d' : 'rgba(93, 74, 125, 0.08)',
                  color: timePeriodFilter === 'evening' ? 'white' : '#5d4a7d',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: timePeriodFilter === 'evening' ? '#4d3e69' : 'rgba(93, 74, 125, 0.15)'
                  }
                }}
              />
              <Chip
                icon={<NightsStay sx={{ fontSize: '1rem' }} />}
                label="Night"
                clickable
                onClick={() => handleTimePeriodChange('night')}
                sx={{ 
                  backgroundColor: timePeriodFilter === 'night' ? '#5d4a7d' : 'rgba(93, 74, 125, 0.08)',
                  color: timePeriodFilter === 'night' ? 'white' : '#5d4a7d',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: timePeriodFilter === 'night' ? '#4d3e69' : 'rgba(93, 74, 125, 0.15)'
                  }
                }}
              />
              <Chip
                label="All"
                clickable
                onClick={() => handleTimePeriodChange('all')}
                sx={{ 
                  backgroundColor: timePeriodFilter === 'all' ? '#5d4a7d' : 'rgba(93, 74, 125, 0.08)',
                  color: timePeriodFilter === 'all' ? 'white' : '#5d4a7d',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: timePeriodFilter === 'all' ? '#4d3e69' : 'rgba(93, 74, 125, 0.15)'
                  }
                }}
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                {getFilteredBusResults().length} buses shown
                {timePeriodFilter !== 'all' && (
                  <Box component="span" sx={{ 
                    display: 'block',
                    mt: 0.5,
                    fontSize: '0.8rem',
                    fontStyle: 'italic',
                    color: 'rgba(0, 0, 0, 0.6)'
                  }}>
                    {timePeriodFilter === 'morning' && 'Showing buses between 4 AM - 12 PM'}
                    {timePeriodFilter === 'afternoon' && 'Showing buses between 12 PM - 4 PM'}
                    {timePeriodFilter === 'evening' && 'Showing buses between 4 PM - 7 PM'}
                    {timePeriodFilter === 'night' && 'Showing buses between 7 PM - 4 AM'}
                  </Box>
                )}
              </Typography>
            </Box>
            
            <Box sx={{ 
              maxWidth: '100%', 
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {/* Card-based layout for search results */}
              {getFilteredBusResults().map((bus, index) => {
                const isLive = busLiveList.find((busLive) => busLive.busId === bus.routeId);
                const iconColor = isLive ? "#4caf50" : "#9e9e9e";
                const duration = calculateDuration(bus.departureTime, bus.arrivalTime);
                
                // Determine if this bus is in the future
                const isPastBus = bus.departureTime < currentTime;
                
                // Reset the badge indicators to proper values based on the current view
                const currentFilteredBuses = getFilteredBusResults().filter(b => b.departureTime >= currentTime);
                // Sort buses by departure time
                currentFilteredBuses.sort((a, b) => a.departureTime - b.departureTime);
                
                // Find position of this bus in the sorted list
                const position = currentFilteredBuses.findIndex(b => b.departureTime === bus.departureTime);
                
                // Get current hour to determine current time period
                const now = new Date();
                const currentHour = now.getHours();
                const currentTimePeriod = 
                  (currentHour >= 5 && currentHour < 12) ? 'morning' :
                  (currentHour >= 12 && currentHour < 17) ? 'afternoon' :
                  (currentHour >= 17 && currentHour < 21) ? 'evening' : 'night';
                
                // Check what time period this bus belongs to
                const busTimePeriod = getTimePeriodForHour(Math.floor(bus.departureTime / 3600));
                
                // Only show badges if:
                // 1. We're in the "all" filter and showing buses from the current time period, or
                // 2. We're viewing the current time period's filter
                const showTimeRelevantBadge = 
                  (timePeriodFilter === 'all' && busTimePeriod === currentTimePeriod) || 
                  (timePeriodFilter === currentTimePeriod);
                
                // Only first 2 buses get "SOON" badge, next 2 get "NEXT" badge
                const isSoon = !isPastBus && showTimeRelevantBadge && position >= 0 && position < 2;
                const isNext = !isPastBus && showTimeRelevantBadge && position >= 2 && position < 4;
                
                return (
                  <Card 
                    key={index} 
                    elevation={0}
                    onClick={() => showModal(bus)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.09)',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s ease'
                      },
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: isSoon ? '1px solid rgba(255, 152, 0, 0.4)' : 
                              isNext ? '1px solid rgba(93, 74, 125, 0.2)' :
                              '1px solid rgba(0,0,0,0.08)',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      backgroundColor: isSoon ? 'rgba(255, 152, 0, 0.05)' :
                                      isNext ? 'rgba(93, 74, 125, 0.02)' :
                                      'white'
                    }}
                  >
                    {/* Colored accent based on status */}
                    <Box sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      backgroundColor: isLive ? 'rgba(76, 175, 80, 0.9)' : 
                                    isSoon ? 'rgba(255, 152, 0, 0.8)' :
                                    isNext ? 'rgba(93, 74, 125, 0.7)' : 
                                    'rgba(158, 158, 158, 0.3)'
                    }} />
                    
                    {/* Simplified indicators - only SOON and NEXT */}
                    {(isSoon || isNext) && (
                      <Box sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        backgroundColor: isSoon ? '#ff9800' : '#5d4a7d',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        borderBottomLeftRadius: '8px',
                        zIndex: 1
                      }}>
                        {isSoon ? 'SOON' : 'NEXT'}
                      </Box>
                    )}

                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, pl: 3 }}>
                      {/* Bus name and route with improved layout */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                        pr: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(93, 74, 125, 0.08)',
                            p: 1,
                            mr: 2
                          }}>
                            <DirectionsBus sx={{ color: '#5d4a7d' }} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2, color: '#333' }}>
                              {bus.busName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                              {bus.routeName}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Live indicator moved to right side */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isLive ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                          borderRadius: '16px',
                          px: isLive ? 1.5 : 0,
                          py: isLive ? 0.5 : 0
                        }}>
                          <NavigationIcon fontSize="small" sx={{ color: iconColor, mr: isLive ? 0.5 : 0 }} />
                          {isLive && (
                            <Typography variant="body2" sx={{ 
                              color: '#2e7d32', 
                              fontWeight: 500,
                              fontSize: '0.75rem'
                            }}>
                              LIVE
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Time information with cleaner layout */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        mt: 1.5
                      }}>
                        {/* Departure time */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                            DEPARTURE
                          </Typography>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 500, 
                            color: '#5d4a7d', 
                            lineHeight: 1.2
                          }}>
                            {formatTime(bus.departureTime)}
                          </Typography>
                        </Box>

                        {/* Journey duration in middle */}
                        <Box sx={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          px: 2
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: '16px',
                            px: 1.5,
                            py: 0.5,
                            mb: 0.5
                          }}>
                            <AccessTime fontSize="small" sx={{ mr: 0.5, color: '#636363', fontSize: '0.875rem' }} />
                            <Typography variant="caption" sx={{ 
                              color: '#636363', 
                              fontWeight: 500 
                            }}>
                              {duration} min
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            height: '1px', 
                            width: '40px', 
                            background: 'linear-gradient(to right, #e0e0e0, rgba(93, 74, 125, 0.4), #e0e0e0)',
                            mt: 0.5
                          }} />
                        </Box>

                        {/* Arrival time */}
                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                            ARRIVAL
                          </Typography>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 500, 
                            color: '#5d4a7d',
                            lineHeight: 1.2
                          }}>
                            {formatTime(bus.arrivalTime)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </>
        )}

        {!errorMessage && searched && busResults.length === 0 && (
          <Box mt={3} display="flex" justifyContent="center">
            <Typography variant="body1" color="text.secondary">
              No results found. Try different stops.
            </Typography>
          </Box>
        )}
      </Paper>
      
      <InfoModal {...infoModalProps} />
    </Box>
  );
};

export default BusRouteSearch; 